import { Box } from "@mui/material";
import type { GridColumnGroup, GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useRef } from "react";
import IntercomLauncherDependantElement from "@entrilia/shared/components/IntercomLauncherDependantElement.tsx";
import {
  type PivotGeneralSettings,
  type PivotRowResponse,
  type PivotRowValueCell,
  type PreviewMode,
  type ReportField,
  type SortField,
  type ValueCell,
} from "../../../../../../../api/types/svTypes.ts";
import useResizeObserver from "../../../hooks/resizeObserver.ts";
import { PreviewDefaultSize } from "../../../utilities/const.ts";
import { isAggregationBased } from "../../../utilities/fieldsHelper.ts";
import PreviewComponentAlert from "../common/PreviewComponentAlert.tsx";
import ValidationState from "../common/ValidationState.tsx";
import BiErrorCodeComponent from "../common/bi-error/BiErrorCodeComponent.tsx";
import { hasAvailableDrillDown } from "../common/hasAvailableDrillDown.ts";
import PivotGrid from "./PivotGrid.tsx";
import type { PivotGridState } from "./hooks/gridState.types.ts";
import {
  computeMaxColumnGroupDepth,
  getDataColumns,
  toGridColumn,
  toGridColumnGroup,
  toGridColumnsForLines,
  wrapMeasureInPlaceholderGroups,
} from "./renderDisplayValue.tsx";
import { enrichTabularFormRows } from "./utils/enrichTabularFormRows.ts";
import { getPivotSortModel } from "./utils/getPivotSortModel.ts";
import { rebuildPivotSortingFields } from "./utils/rebuildPivotSortingFields.ts";

interface Props {
  gridState: PivotGridState;
  values: ReportField[];
  rows: ReportField[];
  columns: ReportField[];
  sorts: SortField[];
  previewMode: PreviewMode;
  settings: PivotGeneralSettings;
  disableCellClick?: boolean;
  onDrillDown?: (field: ReportField, cell: ValueCell) => void;
  onSortingUpdate?: (sortFields: SortField[]) => void;
}

export default function PreviewComponent({
  gridState,
  onDrillDown,
  values,
  sorts,
  rows,
  columns,
  previewMode,
  settings,
  disableCellClick,
  onSortingUpdate,
}: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const size = useResizeObserver(divRef, undefined, previewMode === "maximize" ? undefined : PreviewDefaultSize);

  const sortsRef = useRef(sorts);
  useEffect(() => {
    sortsRef.current = sorts;
  }, [sorts]);

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const gridHasConfiguration = useMemo(() => {
    return rows.length > 0 || columns.length > 0;
  }, [rows, columns]);

  const showTabularForm = settings.showGroupedColumns === true && rows.length > 0;
  const calculatedFields = useMemo(
    () => values.concat(rows, columns).filter(isAggregationBased),
    [columns, rows, values]
  );

  const gridColumns = useMemo(() => {
    return gridState.columns.flatMap((column) => {
      if (column.id === "lines") {
        if (showTabularForm) {
          return toGridColumnsForLines(rows);
        }
        return [toGridColumn(column)];
      }
      return getDataColumns(column).map((c) => toGridColumn(c));
    });
  }, [gridState.columns, showTabularForm, rows]);

  const sortModel = useMemo(
    () =>
      getPivotSortModel(
        sorts.map((s) => s.config),
        values.map((f) => f.config.guid),
        gridColumns.map((column) => column.field)
      ),
    [sorts, values, gridColumns]
  );

  const pivotRows = useMemo((): PivotRowResponse[] => {
    const baseRows = gridState.pivot?.rows || [];
    return showTabularForm ? enrichTabularFormRows(baseRows, rows.length) : baseRows;
  }, [gridState.pivot?.rows, showTabularForm, rows.length]);

  const gridColumnsRef = useRef(gridState.columns);
  useEffect(() => {
    gridColumnsRef.current = gridState.columns;
  }, [gridState.columns]);

  const columnGroups = useMemo((): GridColumnGroup[] => {
    const maxDepth = computeMaxColumnGroupDepth(gridState.columns);
    return gridState.columns.map((column) => {
      // Non-pivot (standalone) measures at the top level: give each its own
      // placeholder-group chain so MUI X renders separate empty cells above
      // them instead of merging adjacent ungrouped columns into one cell.
      if (column.measureGuid !== undefined && maxDepth > 0) {
        return wrapMeasureInPlaceholderGroups(column.id ?? "", maxDepth);
      }
      return toGridColumnGroup(column);
    });
  }, [gridState.columns]);

  const onCellClicked = useCallback(
    (cell: PivotRowValueCell | undefined) => {
      if (!cell || !hasAvailableDrillDown(cell.drillDownId)) {
        return;
      }

      const measure = values.find((f) => f.config.guid === cell.id);
      if (measure === undefined) {
        return;
      }

      onDrillDown?.(measure, cell);
    },
    [values, onDrillDown]
  );

  const onSortModelChange = useCallback(
    (newSorting: GridSortModel) => {
      const sortFields = rebuildPivotSortingFields(
        newSorting,
        valuesRef.current,
        gridColumnsRef.current,
        sortsRef.current
      );
      if (sortFields.length === 0) {
        return;
      }
      onSortingUpdate?.(sortFields);
    },
    [onSortingUpdate]
  );

  return (
    <BiErrorCodeComponent errorMessage={gridState.error}>
      <PreviewComponentAlert error={gridState.error} failedMeasures={gridState.failedMeasures} />
      <ValidationState state={gridState.configurationValidity} fields={calculatedFields}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <IntercomLauncherDependantElement mode="opacity">
            <Box
              ref={divRef}
              sx={(theme) => ({
                display: "flex",
                flex: 1,
                alignItems: "center",
                position: "relative",
                fontFamily: theme.typography.h1.fontFamily,
              })}
            >
              {gridHasConfiguration && (
                <PivotGrid
                  rows={pivotRows}
                  columns={gridColumns}
                  columnGroups={columnGroups}
                  size={size}
                  loading={gridState.calculating}
                  rowFields={rows}
                  onValueCellClick={onCellClicked}
                  grandTotals={gridState.pivot?.grandTotals}
                  showSummaries={settings.rowsGrandTotal}
                  hideGroupRows={showTabularForm && settings.hideGroupRows === true}
                  disableCellClick={!!disableCellClick}
                  onSortModelChange={onSortModelChange}
                  sortModel={sortModel}
                />
              )}
            </Box>
          </IntercomLauncherDependantElement>
        </Box>
      </ValidationState>
    </BiErrorCodeComponent>
  );
}
